'use client'

import { useTransition } from 'react'
import { LuHeart } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'
import { useViewer } from '@/features/viewer/hooks/use-viewer'

import { toggleWish } from '../actions/vehicle'

type WishButtonProps = {
  productId: string
  overlay?: boolean
  labelled?: boolean
  className?: string
}

export default function WishButton({
  productId,
  overlay = false,
  labelled = false,
  className,
}: WishButtonProps) {
  const { viewer, patch, refresh } = useViewer()
  const [pending, startTransition] = useTransition()
  const wished = viewer?.wishIds.includes(productId) ?? false

  const toggle = () => {
    if (pending) return
    const current = viewer?.wishIds ?? []
    void patch({
      wishIds: wished
        ? current.filter((id) => id !== productId)
        : [productId, ...current],
    })
    startTransition(async () => {
      try {
        const saved = await toggleWish(productId)
        if (saved) void patch({ wishIds: saved })
        else void refresh()
      } catch {
        void refresh()
        toast.error('찜 변경에 실패했습니다. 다시 시도해 주세요.')
      }
    })
  }

  const icon = (
    <LuHeart
      aria-hidden="true"
      className={cn('size-5', wished && 'fill-point text-point')}
    />
  )

  if (labelled) {
    return (
      <Button
        type="button"
        variant="outline"
        aria-pressed={wished}
        onClick={toggle}
        className={className}
      >
        {icon}
        찜하기
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-pressed={wished}
      aria-label={wished ? '찜 해제' : '찜하기'}
      onClick={toggle}
      className={cn(
        'rounded-full',
        overlay && 'bg-background/70 hover:bg-background',
        className
      )}
    >
      {icon}
    </Button>
  )
}
